import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { CollectionLeaderboardRow } from './CollectionLeaderboardRow';
import type {
  CollectionLeaderboardQuery,
  CollectionLeaderboardQueryVariables,
  CollectionLeaderboard_userCardCollection,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit) 0;
`;

const COLLECTION_LEADERBOARD_QUERY = gql`
  query CollectionLeaderboardQuery(
    $slug: String!
    $cursor: String
    $sport: Sport!
  ) {
    cardCollection(slug: $slug) {
      slug
      bestByScore(after: $cursor, first: 10) {
        nodes {
          id
          user {
            slug
          }
          ...CollectionLeaderboardRow_userCardCollection
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${CollectionLeaderboardRow.fragments.userCardCollection}
` as TypedDocumentNode<
  CollectionLeaderboardQuery,
  CollectionLeaderboardQueryVariables
>;

type Props = {
  slug: string;
  myUserCardCollection: Nullable<CollectionLeaderboard_userCardCollection>;
};

export const CollectionLeaderboard = ({
  slug,
  myUserCardCollection,
}: Props) => {
  const { sport } = useSportContext();
  const { data, loading, loadMore } = usePaginatedQuery(
    COLLECTION_LEADERBOARD_QUERY,
    {
      variables: {
        slug,
        sport,
      },
      connection: 'UserCardCollectionConnection',
    }
  );
  const pageInfo = data?.cardCollection.bestByScore.pageInfo;
  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        slug,
        cursor: pageInfo?.endCursor,
      });
    },
    !!pageInfo?.hasNextPage,
    loading
  );

  if (loading || !data) {
    return (
      <Root>
        <LoadingIndicator small />
      </Root>
    );
  }

  const allUsersCardCollections = data.cardCollection.bestByScore.nodes;

  const addMyRankToLeaderboard =
    myUserCardCollection &&
    allUsersCardCollections.every(
      ({ user }) => user.slug !== myUserCardCollection.user.slug
    );

  return (
    <Root>
      {allUsersCardCollections.map(userCardCollection => (
        <CollectionLeaderboardRow
          key={userCardCollection.id}
          userCardCollection={userCardCollection}
          collectionSlug={slug}
          highlighted={
            userCardCollection.user.slug === myUserCardCollection?.user.slug
          }
        />
      ))}
      <InfiniteScrollLoader />
      {!!addMyRankToLeaderboard && (
        <CollectionLeaderboardRow
          key={myUserCardCollection.id}
          userCardCollection={myUserCardCollection}
          collectionSlug={slug}
          highlighted
        />
      )}
    </Root>
  );
};

CollectionLeaderboard.fragments = {
  userCardCollection: gql`
    fragment CollectionLeaderboard_userCardCollection on UserCardCollection {
      id
      ...CollectionLeaderboardRow_userCardCollection
    }
    ${CollectionLeaderboardRow.fragments.userCardCollection}
  ` as TypedDocumentNode<CollectionLeaderboard_userCardCollection>,
};
